import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { UsersService } from './users.service';
import { GenericService, LoaderConfig } from './generic.service';
import { ServerResponse } from '../models/ServerResponse';
import { Observable } from 'rxjs';
import { CreateTicket, TicketTyping } from '../models/Ticket';
import { CreateElectricityMeterES, CreateElectricityMeterPT, CreateNaturalGasMeterPT, ElectricityMeter, GasMeter } from '../models/EnergyMeter';
import { CreateElectricityMeterConsumption, CreateNaturalGasMeterConsumption, ElectricityMeterConsumption, NaturalGasMeterConsumption } from '../models/EnergyConsumptions';
import { ISAPI, ISPagination } from 'is-lib';
import { RequestCacheConfig } from '../models/Request';

@Injectable({
    providedIn: 'root'
})
export class EnergyMetersService {
    
    constructor(private genericService: GenericService, private _user: UsersService) {}

    //#region Electricity
    //#region Electricity Meters
    getElectricityMeterByCode(electricityMeterCode: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<ElectricityMeter>> {
        return this.genericService.get(`${environment.apiUrl}ElectricityMeters/${this._user.userCountryCode}/ByCode/${electricityMeterCode}`, loaderConfig);
    }

    getElectricityMeterConsumptions(electricityMeterId: number, dateFrom: string, dateTo: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<ElectricityMeterConsumption[]>> {
        return this.genericService.get(`${environment.apiUrl}ElectricityMeters/${this._user.userCountryCode}/${electricityMeterId}/Consumptions/${dateFrom}/${dateTo}`, true, loaderConfig);
    }

    postElectricityMeter(body: CreateElectricityMeterPT | CreateElectricityMeterES, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this.genericService.post(`${environment.apiUrl}ElectricityMeters/${this._user.userCountryCode}`, body, undefined, loaderConfig)
    }

    postElectricityMeterConsumptions(electricityMeterId: number, body: CreateElectricityMeterConsumption[], loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this.genericService.post(`${environment.apiUrl}ElectricityMeters/${this._user.userCountryCode}/${electricityMeterId}/Consumptions`, body, undefined, loaderConfig)
    }
    //#endregion

    //#region Power
    getContractedPowers(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}Electricity/${this._user.userCountryCode}/ContractedPowers`, loaderConfig, undefined);
    }
    //#endregion

    //#region Tension Levels
    getTensionLevels(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}Electricity/${this._user.userCountryCode}/TensionLevels`, loaderConfig, undefined);
    }
    //#endregion

    //#region Counting Cycles
    getCountingCycles(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}Electricity/${this._user.userCountryCode}/CountingCycles`, loaderConfig, undefined, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion

    //#region Time Cycles
    getTimeCycles(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}Electricity/${this._user.userCountryCode}/TimeCycles`, loaderConfig, undefined, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion

    getElectricityTariffs(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.post(`${environment.apiUrl}Electricity/${this._user.userCountryCode}/Tariffs`, body, undefined, loaderConfig, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion

    //#region Natural Gas Meters
    getNaturalGasMeterByCode(naturalGasMeterCode: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<GasMeter>> {
        return this.genericService.get(`${environment.apiUrl}NaturalGasMeters/${this._user.userCountryCode}/ByCode/${naturalGasMeterCode}`, loaderConfig);
    }

    getNaturalGasMeterConsumptions(naturalGasMeterId: number, dateFrom: string, dateTo: string, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<NaturalGasMeterConsumption[]>> {
        return this.genericService.get(`${environment.apiUrl}NaturalGasMeters/${this._user.userCountryCode}/${naturalGasMeterId}/Consumptions/${dateFrom}/${dateTo}`, loaderConfig);
    }

    postNaturalGasMeter(body: CreateNaturalGasMeterPT, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this.genericService.post(`${environment.apiUrl}NaturalGasMeters/${this._user.userCountryCode}`, body, undefined, loaderConfig)
    }

    postNaturalGasMeterConsumptions(electricityMeterId: number, body: CreateNaturalGasMeterConsumption[], loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any>> {
        return this.genericService.post(`${environment.apiUrl}NaturalGasMeters/${this._user.userCountryCode}/${electricityMeterId}/Consumptions`, body, undefined, loaderConfig )
    }

    //#region Pressure Level
    getPressureLevels(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}NaturalGas/${this._user.userCountryCode}/PressureLevels`, loaderConfig, undefined, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion

    //#region Tiers
    getTiers(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}NaturalGas/${this._user.userCountryCode}/Tiers`, loaderConfig, undefined, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion

    //#region Tariffs
    getTariffs(body?: ISAPI, loaderConfig: LoaderConfig | boolean = false): Observable<ServerResponse<any[]>> {
        if (!body) {
            body = new ISAPI( [], new ISPagination(999, 1) )
        }

        return this.genericService.get(`${environment.apiUrl}NaturalGas/${this._user.userCountryCode}/TariffOptions`, loaderConfig, undefined, new RequestCacheConfig(1, undefined, 'hours'));
    }
    //#endregion
    //#endregion

    
}